import { toast } from "sonner";

export const SHOPIFY_API_VERSION = "2025-07";
export const SHOPIFY_STORE_PERMANENT_DOMAIN = "vu0gbj-sg.myshopify.com";
export const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
export const SHOPIFY_STOREFRONT_TOKEN = "67a7a285eea116cc20474400a7e1dae9";

export interface ShopifyImage {
  url: string;
  altText: string | null;
}

export interface ShopifyVariant {
  id: string;
  title: string;
  price: { amount: string; currencyCode: string };
  availableForSale: boolean;
  selectedOptions: Array<{ name: string; value: string }>;
}

export interface ShopifyVideoSource {
  url: string;
  mimeType: string;
  format: string;
}

export interface ShopifyMedia {
  mediaContentType: "IMAGE" | "VIDEO" | "EXTERNAL_VIDEO" | "MODEL_3D";
  alt: string | null;
  previewImage?: { url: string } | null;
  // VIDEO
  sources?: ShopifyVideoSource[];
  // MODEL_3D
  modelSources?: Array<{ url: string; mimeType: string; format: string }>;
}

export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    productType?: string;
    tags?: string[];
    priceRange: { minVariantPrice: { amount: string; currencyCode: string } };
    images: { edges: Array<{ node: ShopifyImage }> };
    variants: { edges: Array<{ node: ShopifyVariant }> };
    options: Array<{ name: string; values: string[] }>;
    media?: { edges: Array<{ node: ShopifyMedia }> };
  };
}

export async function storefrontApiRequest<T = any>(
  query: string,
  variables: Record<string, any> = {},
): Promise<{ data?: T } | undefined> {
  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (response.status === 402) {
    toast.error("Shopify: Payment required", {
      description:
        "Shopify API access requires an active Shopify billing plan. Visit https://admin.shopify.com to upgrade.",
    });
    return;
  }

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();
  if (data.errors) {
    throw new Error(
      `Error calling Shopify: ${data.errors.map((e: any) => e.message).join(", ")}`,
    );
  }
  return data;
}

const MEDIA_FRAGMENT = `
  media(first: 10) {
    edges {
      node {
        mediaContentType
        alt
        previewImage { url }
        ... on Video {
          sources { url mimeType format }
        }
        ... on Model3d {
          sources { url mimeType format }
        }
      }
    }
  }
`;

export const PRODUCTS_QUERY = `
  query GetProducts($first: Int!, $query: String) {
    products(first: $first, query: $query) {
      edges {
        node {
          id
          title
          description
          handle
          productType
          tags
          priceRange { minVariantPrice { amount currencyCode } }
          images(first: 5) { edges { node { url altText } } }
          variants(first: 10) {
            edges {
              node {
                id
                title
                price { amount currencyCode }
                availableForSale
                selectedOptions { name value }
              }
            }
          }
          options { name values }
          ${MEDIA_FRAGMENT}
        }
      }
    }
  }
`;

export const PRODUCT_BY_HANDLE_QUERY = `
  query GetProductByHandle($handle: String!) {
    product(handle: $handle) {
      id
      title
      description
      descriptionHtml
      handle
      productType
      tags
      priceRange { minVariantPrice { amount currencyCode } }
      images(first: 10) { edges { node { url altText } } }
      variants(first: 20) {
        edges {
          node {
            id
            title
            price { amount currencyCode }
            availableForSale
            selectedOptions { name value }
          }
        }
      }
      options { name values }
      ${MEDIA_FRAGMENT}
    }
  }
`;

export async function fetchProducts(first = 24, query?: string) {
  const data = await storefrontApiRequest<{ products: { edges: ShopifyProduct[] } }>(
    PRODUCTS_QUERY,
    { first, query },
  );
  const edges = data?.data?.products?.edges ?? [];
  return edges.filter((edge) =>
    edge.node.variants.edges.some((v) => v.node.availableForSale),
  );
}

export async function fetchProductByHandle(handle: string) {
  const data = await storefrontApiRequest<{ product: ShopifyProduct["node"] & { descriptionHtml: string; media?: { edges: Array<{ node: ShopifyMedia }> } } }>(
    PRODUCT_BY_HANDLE_QUERY,
    { handle },
  );
  return data?.data?.product ?? null;
}

export function getProductVideo(product: ShopifyProduct["node"]): ShopifyVideoSource | null {
  const videoMedia = product.media?.edges.find((e) => e.node.mediaContentType === "VIDEO");
  if (!videoMedia) return null;
  const sources = videoMedia.node.sources ?? [];
  // Prefer mp4 for broad compatibility
  return (
    sources.find((s) => s.mimeType === "video/mp4") ??
    sources[0] ??
    null
  );
}

export function getProductModel3d(product: ShopifyProduct["node"]): { url: string; alt: string | null; poster?: string } | null {
  const m = product.media?.edges.find((e) => e.node.mediaContentType === "MODEL_3D");
  if (!m) return null;
  const glb = m.node.sources?.find((s) => s.format === "glb") ?? m.node.sources?.[0];
  if (!glb) return null;
  return { url: glb.url, alt: m.node.alt, poster: m.node.previewImage?.url };
}

const JEWELRY_RX = /\b(ring|band|earring|stud|pendant|necklace|bracelet|bangle|brooch|tennis|chain|setting|engagement)\b/i;
const STONE_RX = /\b(loose|stone|melee|solitaire diamond|round brilliant|emerald cut|oval cut|cushion cut|princess cut|pear cut|carat\s+(loose|diamond))\b/i;

export type ProductCategory = "jewelry" | "stone";

export function getProductCategory(product: ShopifyProduct): ProductCategory {
  const node = product.node;
  const haystack = `${node.productType ?? ""} ${(node.tags ?? []).join(" ")} ${node.title}`;
  if (JEWELRY_RX.test(haystack)) return "jewelry";
  if (STONE_RX.test(haystack)) return "stone";
  // Fall back: if title is just a carat/shape descriptor, treat as a loose stone.
  if (/\b\d+(\.\d+)?\s*ct\b/i.test(node.title)) return "stone";
  return "jewelry";
}

export function formatPrice(amount: string | number, currencyCode = "USD") {
  const value = typeof amount === "string" ? parseFloat(amount) : amount;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${currencyCode} ${value.toFixed(2)}`;
  }
}
